// Where the houses stand, read off the snapshot (`globals.diplomacy`, module
// `diplomacy.basic`). No client copy: an answered or expired offer just leaves.
import type { GameSnapshot } from '@aod/protocol';
import type { TreatyKind } from '../sim/useSimWorld/useTreatyIntents.js';

export type Relation = 'war' | 'neutral' | 'pact' | 'alliance' | 'federation';

export interface TreatyOffer {
  readonly from: number;
  readonly to: number;
  /** The relation the offer would create. */
  readonly state: Relation;
  readonly expiresTick: number;
}

export interface Diplomacy {
  readonly relations: ReadonlyArray<{ a: number; b: number; state: Relation }>;
  readonly proposals: readonly TreatyOffer[];
  readonly points: ReadonlyArray<{ seat: number; points: number; max: number; honor: number }>;
  /** Diplomacy points one offer costs. */
  readonly actionCost: number;
  /** What betraying a pact or an alliance costs in honor (a marriage costs more). */
  readonly breakHonor: {
    readonly pact: number;
    readonly alliance: number;
    readonly marriage: number;
  };
}

export function readDiplomacy(snapshot: GameSnapshot | null): Diplomacy | null {
  const globals = snapshot?.state.globals as { diplomacy?: Diplomacy } | undefined;
  return globals?.diplomacy ?? null;
}

/** How two houses stand; neutral when the sim does not say. */
export function relationBetween(d: Diplomacy | null, x: number, y: number): Relation {
  const hit = d?.relations.find((r) => (r.a === x && r.b === y) || (r.a === y && r.b === x));
  return hit?.state ?? 'neutral';
}

/** The offer `other` has made to `me`, if one is waiting. */
export function offerFrom(d: Diplomacy | null, me: number, other: number): TreatyOffer | null {
  return d?.proposals.find((p) => p.from === other && p.to === me) ?? null;
}

/** Every offer waiting on `me`. */
export function offersTo(d: Diplomacy | null, me: number): readonly TreatyOffer[] {
  return d?.proposals.filter((p) => p.to === me) ?? [];
}

/** Is an offer of mine out to `other`? */
export function offerSent(d: Diplomacy | null, me: number, other: number): boolean {
  return d?.proposals.some((p) => p.from === me && p.to === other) ?? false;
}

/** The next rung up from where two houses stand; null at the top or at a federation. */
export function nextRung(relation: Relation): TreatyKind | null {
  switch (relation) {
    case 'war':
      return 'peace';
    case 'neutral':
      return 'pact';
    case 'pact':
      return 'alliance';
    default:
      return null;
  }
}

export function pointsOf(d: Diplomacy | null, seat: number): number {
  return d?.points.find((p) => p.seat === seat)?.points ?? 0;
}

/** Honor lost by declaring war from here; 0 from peace, where it is no betrayal. */
export function warHonorCost(d: Diplomacy, relation: Relation, married: boolean): number {
  if (relation === 'pact') return d.breakHonor.pact;
  if (relation === 'alliance') return d.breakHonor.alliance + (married ? d.breakHonor.marriage : 0);
  return 0;
}

export type TreatyNewsKind =
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'war'
  | 'wed'
  | 'refused'
  | 'lapsed'
  /** Your own answer took: the offer became the relation it promised. */
  | 'taken'
  /** Your own answer did not take: the offer left the table, nothing changed. */
  | 'void'
  /** The sim would not take your answer: the offer was already gone. */
  | 'stale';

export interface TreatyNews {
  readonly kind: TreatyNewsKind;
  readonly other: number;
  /** The relation the offer would have made (or, for war, 'war'). */
  readonly state: Relation;
}

/** An offer the player has answered, waiting for the sim to act on it. */
export interface AnsweredOffer {
  /** The house that offered. */
  readonly other: number;
  /** The relation the offer would have made. */
  readonly state: Relation;
  /** Whether the player said yes. */
  readonly accept: boolean;
  /** The tick the offer lapsed on. */
  readonly expiresTick: number;
}

/**
 * What became of the offers the player answered.
 *
 * The sim publishes no event for an answer, so the table is the only word on
 * it: the offer leaves once it is answered, and the relation says whether the
 * answer took. An offer the player declined leaves the table too — that is
 * the player's own doing and says nothing, so it says nothing here.
 */
export function answerNews(
  next: Diplomacy | null,
  me: number,
  answered: readonly AnsweredOffer[],
): TreatyNews[] {
  if (!next) return [];
  const out: TreatyNews[] = [];
  for (const a of answered) {
    const still = next.proposals.some((p) => p.from === a.other && p.to === me);
    if (still) continue;
    if (relationBetween(next, me, a.other) === a.state) {
      out.push({ kind: 'taken', other: a.other, state: a.state });
    } else if (a.accept) {
      out.push({ kind: 'void', other: a.other, state: a.state });
    }
  }
  return out;
}

/**
 * What changed for `me` between two snapshots: my offers that were answered or
 * lapsed, and wars. An offer simply vanishing from the table says nothing, so
 * this reads the relation and the clock to tell the three endings apart.
 */
export function treatyNews(
  prev: Diplomacy | null,
  next: Diplomacy | null,
  me: number,
  tick: number,
): TreatyNews[] {
  if (!prev || !next) return [];
  const out: TreatyNews[] = [];
  for (const offer of prev.proposals) {
    if (offer.from !== me) continue;
    if (next.proposals.some((p) => p.from === offer.from && p.to === offer.to)) continue;
    const now = relationBetween(next, me, offer.to);
    const kind: TreatyNewsKind =
      now === offer.state ? 'accepted' : tick >= offer.expiresTick ? 'expired' : 'declined';
    out.push({ kind, other: offer.to, state: offer.state });
  }
  for (const r of next.relations) {
    if (r.a !== me && r.b !== me) continue;
    const other = r.a === me ? r.b : r.a;
    if (r.state === 'war' && relationBetween(prev, me, other) !== 'war') {
      out.push({ kind: 'war', other, state: 'war' });
    }
  }
  return out;
}
